import { create } from "zustand";

import type {
  AgendaItem,
  AgendaKind,
  CustomerSafeTicket,
  StageMode,
  StageTheme,
  TicketSummaryDto,
  UpcomingEvent,
} from "@msc/shared";

import { DECK_MAX } from "../lib/customerView/deckLimit.js";
import type { GroupKey } from "../lib/customerView/ticketView.js";

/**
 * Zustand des Präsentationsmodus. Owner ist IMMER das Cockpit-Fenster; das
 * Stage-Fenster rendert nur Snapshots vom Bühnenkanal (`presentChannel.ts`).
 *
 * `phase`-Ablauf: idle → prep (Deck-Auswahl) → generating (KI-Zusammenfassungen)
 * → review (Zusammenfassungen prüfen) → presenting (Cockpit + Bühne) → idle.
 *
 * Kernfelder werden in sessionStorage gespiegelt, damit ein Cockpit-Reload
 * mitten im Termin den Zustand wiederherstellt (Spec: Fehlerfälle).
 *
 * Port aus der Hub-App (`jira-customer-view/frontend/src/store/presentation.ts`);
 * Abweichungen: Storage-Schlüssel `msc-cv-presentation`, Typen aus `@msc/shared`,
 * `move(key, delta)` statt Drag-and-drop-`reorder`, `buildStageSnapshot` lebt als
 * reine Funktion in `../lib/customerView/stageSnapshot.js`.
 */
export type PresentationPhase = "idle" | "prep" | "generating" | "review" | "presenting";

export interface ShownAttachment {
  ticketKey: string;
  id: string;
  filename: string;
}

export interface FocusedComment {
  ticketKey: string;
  id: string;
}

interface PresentationState {
  phase: PresentationPhase;
  /**
   * Kennung DIESER Präsentation, beim Start vergeben (`startShow`). Das
   * Kundenfenster bekommt sie in der Adresse und hört am Kanal nur auf dieses
   * Cockpit; jeder andere CRM-Tab derselben Umgebung bleibt stumm. Leer
   * außerhalb einer laufenden Präsentation.
   */
  ownerId: string;
  /** Alle Kandidaten in Präsentationsreihenfolge (Prep: sichtbare Liste). */
  deckKeys: string[];
  /** Auswahl-Flags je Ticket-Key (Prep). */
  selected: Record<string, boolean>;
  /** Finales Deck (nur ausgewählte, in Reihenfolge) — ab "generating". */
  deck: string[];
  mode: StageMode;
  currentIndex: number;
  summaries: Record<string, TicketSummaryDto>;
  events: UpcomingEvent[] | null;
  /**
   * Bühnen-Design (dunkel/hell/Kundendesign) — unabhängig vom App-Theme des
   * Cockpits. `customer` zeichnet die Bühne nur, wenn der Snapshot auch ein
   * Design trägt; sonst fällt sie auf dunkel (`resolveStageTheme`).
   */
  stageTheme: StageTheme;
  /** Vom Lead explizit für die Bühne freigegebene INTERNE Kommentare (Ids je Ticket). */
  releasedInternal: Record<string, string[]>;
  /** Weiterblättern hakt das verlassene Ticket automatisch ab. */
  autoReview: boolean;
  /**
   * Sollen vor der Präsentation KI-Zusammenfassungen erzeugt werden? Aus
   * heißt: kein LLM-Aufruf, die Folie fällt auf den letzten öffentlichen
   * Kommentar zurück (s. `buildStageSnapshot`). Wie `autoReview` eine
   * Vorliebe des Leads — `startPrep` und `reset` lassen sie stehen.
   */
  withSummaries: boolean;
  /** Interne Meeting-Notizen je Ticket (erreichen NIE die Bühne). */
  notes: Record<string, string>;
  /** Bild-Anhang, der gerade groß auf der Bühne liegt. */
  shownAttachment: ShownAttachment | null;
  /**
   * Kommentar, der gerade groß auf der Bühne liegt (#272) — damit Lead und
   * Kunde über GENAU diesen reden. Schließt ein gezeigtes Bild aus und
   * umgekehrt; jeder Folienwechsel hebt ihn auf. Nicht persistiert, wie das
   * Bild.
   */
  focusedComment: FocusedComment | null;
  /**
   * Gruppe je Ticket, so wie die Liste beim Start der Vorbereitung gruppiert
   * war (#278) — EINGEFROREN: Ein Statuswechsel mitten im Termin ließe die
   * Agenda sonst umspringen. Leer, wenn die Liste ungruppiert war.
   */
  deckGroups: Record<string, string>;
  /** Wonach `deckGroups` gruppiert — für Nachzügler, die erst im Termin dazukommen. */
  deckGroupBy: GroupKey;
  /**
   * Die Agenda-Punkte dieses Termins (D9) — aus der Vorlage des Bereichs
   * geladen und vom Lead angepasst. Abgewählte Punkte bleiben in der Liste
   * (der Lead soll sie wieder anschalten können), sie laufen nur nicht mit.
   */
  agendaItems: AgendaItem[];
  /**
   * Der Kunden-Objektschlüssel, aus dessen Vorlagen `agendaItems` stammen —
   * `null`, solange nichts geladen wurde. Die Vorbereitung übernimmt die
   * Vorlagen nur, wenn der Store noch keine hat ODER die vorhandenen zu einem
   * ANDEREN Kunden gehören: Sonst stünde beim Kundenwechsel die Wartung des
   * vorigen Termins auf der Folie, und niemand sähe es der Liste an.
   */
  agendaObjectKey: string | null;
  /**
   * Index in die AKTIVIERTEN Punkte — dieselbe Zählung wie im Snapshot
   * (`buildStageSnapshot` gibt nur die aktivierten auf die Bühne). −1
   * außerhalb des Modus `agendaItem`.
   */
  agendaIndex: number;

  /**
   * Vorbereitung starten. `grouping` trägt die Gruppierung der Liste, aus der
   * die Präsentation startet; ohne sie läuft das Deck ungruppiert.
   */
  startPrep: (
    visibleKeys: string[],
    grouping?: { by: GroupKey; labels: Record<string, string> },
    /** Kandidaten, die abgewählt starten — die Steering-Tickets (#336). */
    abgewaehlt?: string[],
  ) => void;
  cancelPrep: () => void;
  toggleSelect: (key: string) => void;
  selectAll: (on: boolean) => void;
  /** Zusätzlich alle Tickets mit diesem Status auswählen (additiv). */
  selectByStatus: (status: string, tickets: CustomerSafeTicket[]) => void;
  /** Verschiebt einen Kandidaten in der Prep-Liste um `delta` Positionen. */
  move: (key: string, delta: -1 | 1) => void;
  beginGenerating: () => void;
  /**
   * Generierung fertig -> Review-Schritt (Zusammenfassungen prüfen).
   * `events` ist `null`, wenn die Termine nicht geladen werden konnten — sie
   * sind fail-soft, die Endfolie lässt ihr Raster dann weg.
   */
  beginReview: (summaries: TicketSummaryDto[], events: UpcomingEvent[] | null) => void;
  backToPrep: () => void;
  setSummary: (dto: TicketSummaryDto) => void;
  /**
   * Review bestätigt -> Kundenfenster öffnen, Präsentation läuft. Hat der
   * Kunde ein Design (D10), startet die Bühne darin; ohne Design fällt ein
   * noch stehendes `customer` aus einem früheren Termin auf dunkel zurück,
   * hell oder dunkel bleiben, wie der Lead sie zuletzt gewählt hat.
   */
  startShow: (hasCustomerBranding?: boolean) => void;
  goTo: (index: number) => void;
  next: () => void;
  prev: () => void;
  pause: () => void;
  resume: () => void;
  end: () => void;
  reset: () => void;
  /** Nächstes Bühnen-Design: dunkel → hell → Kundendesign → dunkel (s. `nextStageTheme`). */
  cycleStageTheme: (hasCustomer: boolean) => void;
  toggleInternalRelease: (ticketKey: string, commentId: string) => void;
  toggleAutoReview: () => void;
  setWithSummaries: (on: boolean) => void;
  setNote: (ticketKey: string, text: string) => void;
  /** Entfernt die Notiz zu EINEM Ticket — für erfolgreich gepostete Notizen (Teilfehler). */
  clearNote: (ticketKey: string) => void;
  clearNotes: () => void;
  showAttachment: (a: ShownAttachment | null) => void;
  focusComment: (c: FocusedComment | null) => void;
  /** Ticket während der laufenden Präsentation ans Deck anhängen. */
  addToDeck: (ticketKey: string, group?: string | null) => void;
  /**
   * Die geladenen Agenda-Punkte übernehmen (Vorbereitung). `objectKey` hält
   * fest, zu WELCHEM Kunden sie gehören — ohne Angabe gilt die Herkunft als
   * unbekannt (`null`), und die Vorbereitung lädt beim nächsten Kunden neu.
   */
  setAgendaItems: (items: AgendaItem[], objectKey?: string | null) => void;
  /** Titel, Text oder an/aus EINES Punktes ändern. */
  updateAgendaItem: (
    kind: AgendaKind,
    patch: Partial<Pick<AgendaItem, "title" | "body" | "enabled">>,
  ) => void;
  /** Direkt auf eine Agenda-Folie springen; `i` zählt über die aktivierten Punkte. */
  goToAgendaItem: (i: number) => void;
}

const STAGE_THEMES: readonly StageTheme[] = ["dark", "light", "customer"];

/**
 * Der Theme-Knopf des Cockpits: dunkel → hell → Kundendesign → dunkel. Ohne
 * Kundendesign wird `customer` übersprungen — und ein stehengebliebenes
 * `customer` geht zurück auf dunkel. Rein, damit der Knopf den NÄCHSTEN
 * Zustand beschriften kann, ohne ihn herbeizuführen.
 */
export function nextStageTheme(current: StageTheme, hasCustomer: boolean): StageTheme {
  if (current === "dark") return "light";
  if (current === "light") return hasCustomer ? "customer" : "dark";
  return "dark";
}

/** Ein gespeicherter Wert aus einer anderen Fassung (oder von Hand verändert) wird zu „dark". */
function validStageTheme(value: unknown): StageTheme {
  return STAGE_THEMES.includes(value as StageTheme) ? (value as StageTheme) : "dark";
}

/** Die Punkte, die im Termin wirklich laufen — Reihenfolge der Vorlage. */
function enabledAgenda(items: AgendaItem[]): AgendaItem[] {
  return items.filter((a) => a.enabled);
}

const STORAGE_KEY = "msc-cv-presentation";

interface PersistedSlice {
  phase: PresentationPhase;
  ownerId?: string;
  deck: string[];
  mode: StageMode;
  currentIndex: number;
  summaries: Record<string, TicketSummaryDto>;
  events: UpcomingEvent[] | null;
  stageTheme?: StageTheme;
  releasedInternal?: Record<string, string[]>;
  autoReview?: boolean;
  withSummaries?: boolean;
  notes?: Record<string, string>;
  /** Optional: Ein Eintrag aus einer Fassung vor D9 kennt sie nicht. */
  agendaItems?: AgendaItem[];
  agendaObjectKey?: string | null;
  agendaIndex?: number;
  deckGroups?: Record<string, string>;
  deckGroupBy?: GroupKey;
}

function persist(s: PresentationState): void {
  try {
    const slice: PersistedSlice = {
      phase: s.phase,
      ownerId: s.ownerId,
      deck: s.deck,
      mode: s.mode,
      currentIndex: s.currentIndex,
      summaries: s.summaries,
      events: s.events,
      stageTheme: s.stageTheme,
      releasedInternal: s.releasedInternal,
      autoReview: s.autoReview,
      withSummaries: s.withSummaries,
      notes: s.notes,
      agendaItems: s.agendaItems,
      agendaObjectKey: s.agendaObjectKey,
      agendaIndex: s.agendaIndex,
      deckGroups: s.deckGroups,
      deckGroupBy: s.deckGroupBy,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(slice));
  } catch {
    /* Storage nicht verfügbar — Reload-Komfort entfällt, mehr nicht */
  }
}

function loadPersisted(): PersistedSlice | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PersistedSlice;
    // Nur eine laufende Präsentation ist es wert, wiederhergestellt zu werden.
    if (p.phase !== "presenting" || !Array.isArray(p.deck)) return null;
    return p;
  } catch {
    return null;
  }
}

const restored = loadPersisted();

/** Neue Präsentations-Kennung; `randomUUID` fehlt nur in sehr alten Browsern. */
function newOwnerId(): string {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export const usePresentationStore = create<PresentationState>((set, get) => {
  /** set + persist in einem Schritt (Owner-Fenster spiegelt nach jedem Update). */
  const update = (partial: Partial<PresentationState>) => {
    set(partial as PresentationState);
    persist(get());
  };

  return {
    phase: restored?.phase ?? "idle",
    ownerId: restored?.ownerId ?? "",
    deckKeys: [],
    selected: {},
    deck: restored?.deck ?? [],
    mode: restored?.mode ?? "agenda",
    currentIndex: restored?.currentIndex ?? -1,
    summaries: restored?.summaries ?? {},
    events: restored?.events ?? null,
    stageTheme: validStageTheme(restored?.stageTheme),
    releasedInternal: restored?.releasedInternal ?? {},
    autoReview: restored?.autoReview ?? true,
    withSummaries: restored?.withSummaries ?? true,
    notes: restored?.notes ?? {},
    shownAttachment: null,
    focusedComment: null,
    deckGroups: restored?.deckGroups ?? {},
    deckGroupBy: restored?.deckGroupBy ?? "none",
    agendaItems: restored?.agendaItems ?? [],
    agendaObjectKey: restored?.agendaObjectKey ?? null,
    agendaIndex: restored?.agendaIndex ?? -1,

    startPrep: (visibleKeys, grouping, abgewaehlt = []) =>
      update({
        phase: "prep",
        deckKeys: visibleKeys,
        // Standard: alle an (Entscheidung aus dem Brainstorming) — außer Steering (#336).
        selected: Object.fromEntries(visibleKeys.map((k) => [k, !abgewaehlt.includes(k)])),
        deckGroups: grouping?.labels ?? {},
        deckGroupBy: grouping?.by ?? "none",
      }),
    cancelPrep: () => update({ phase: "idle", deckKeys: [], selected: {} }),
    toggleSelect: (key) =>
      update({ selected: { ...get().selected, [key]: !get().selected[key] } }),
    selectAll: (on) =>
      update({
        selected: Object.fromEntries(get().deckKeys.map((k) => [k, on])),
      }),
    selectByStatus: (status, tickets) => {
      const next = { ...get().selected };
      for (const t of tickets) if (t.status === status) next[t.key] = true;
      update({ selected: next });
    },
    move: (key, delta) => {
      const keys = [...get().deckKeys];
      const idx = keys.indexOf(key);
      if (idx < 0) return;
      const swapIdx = idx + delta;
      if (swapIdx < 0 || swapIdx >= keys.length) return;
      const tmp = keys[idx] as string;
      keys[idx] = keys[swapIdx] as string;
      keys[swapIdx] = tmp;
      update({ deckKeys: keys });
    },
    beginGenerating: () =>
      update({
        phase: "generating",
        deck: get().deckKeys.filter((k) => get().selected[k]),
      }),
    beginReview: (summaries, events) =>
      update({
        phase: "review",
        summaries: Object.fromEntries(summaries.map((s) => [s.issueKey, s])),
        events,
      }),
    backToPrep: () => update({ phase: "prep" }),
    setSummary: (dto) =>
      update({ summaries: { ...get().summaries, [dto.issueKey]: dto } }),
    startShow: (hasCustomerBranding = false) => {
      const current = get().stageTheme;
      // `=== true`: Wer `startShow` einmal direkt als onClick verdrahtet,
      // reichte sonst das Klick-Ereignis als „hat Design" durch.
      const stageTheme: StageTheme = hasCustomerBranding === true
        ? "customer"
        : current === "customer"
          ? "dark"
          : current;
      update({
        phase: "presenting",
        ownerId: newOwnerId(),
        mode: "agenda",
        currentIndex: -1,
        agendaIndex: -1,
        stageTheme,
      });
    },
    goTo: (index) => {
      const len = get().deck.length;
      if (index < 0 || index >= len) return;
      update({ mode: "ticket", currentIndex: index, agendaIndex: -1, shownAttachment: null, focusedComment: null });
    },
    next: () => {
      const { mode, currentIndex, deck, agendaItems, agendaIndex } = get();
      /**
       * Hinter den Agenda-Folien geht es ins Deck — und wenn keines übrig
       * ist, direkt aufs Ende. Ein Deck der Länge 0 entsteht, wenn der Lead
       * in der Vorbereitung alles abwählt; ohne diesen Zweig stünde er auf
       * „Ticket 0", das es nicht gibt.
       */
      const nachDerAgenda = () =>
        deck.length > 0
          ? { mode: "ticket" as const, currentIndex: 0, agendaIndex: -1, shownAttachment: null, focusedComment: null }
          : { mode: "end" as const, currentIndex: -1, agendaIndex: -1, shownAttachment: null, focusedComment: null };
      if (mode === "agenda") {
        update(
          enabledAgenda(agendaItems).length > 0
            ? { mode: "agendaItem", agendaIndex: 0, currentIndex: -1, shownAttachment: null, focusedComment: null }
            : nachDerAgenda(),
        );
      } else if (mode === "agendaItem") {
        update(
          agendaIndex + 1 < enabledAgenda(agendaItems).length
            ? { agendaIndex: agendaIndex + 1, shownAttachment: null, focusedComment: null }
            : nachDerAgenda(),
        );
      } else if (mode === "ticket" && currentIndex < deck.length - 1)
        update({ currentIndex: currentIndex + 1, shownAttachment: null, focusedComment: null });
      else if (mode === "ticket") update({ mode: "end", currentIndex: -1, shownAttachment: null, focusedComment: null });
    },
    prev: () => {
      const { mode, currentIndex, agendaItems, agendaIndex } = get();
      /** Spiegelbild zu `nachDerAgenda`: von Ticket 0 zurück auf den LETZTEN aktivierten Punkt. */
      const vorDenTickets = () => {
        const anzahl = enabledAgenda(agendaItems).length;
        return anzahl > 0
          ? { mode: "agendaItem" as const, agendaIndex: anzahl - 1, currentIndex: -1, shownAttachment: null, focusedComment: null }
          : { mode: "agenda" as const, currentIndex: -1, agendaIndex: -1, shownAttachment: null, focusedComment: null };
      };
      if (mode === "ticket" && currentIndex > 0)
        update({ currentIndex: currentIndex - 1, shownAttachment: null, focusedComment: null });
      else if (mode === "ticket") update(vorDenTickets());
      else if (mode === "agendaItem")
        update(
          agendaIndex > 0
            ? { agendaIndex: agendaIndex - 1, shownAttachment: null, focusedComment: null }
            : { mode: "agenda", agendaIndex: -1, currentIndex: -1, shownAttachment: null, focusedComment: null },
        );
      else if (mode === "end")
        update({ mode: "ticket", currentIndex: get().deck.length - 1 });
    },
    pause: () => update({ mode: "pause" }),
    /**
     * Aus der Pause zurück auf die Folie, auf der der Termin stand — sonst
     * landete ein Lead, der auf einer Agenda-Folie pausiert, anschließend auf
     * der Übersicht. `agendaIndex` ist außerhalb des Modus `agendaItem` −1
     * und beantwortet die Frage damit eindeutig.
     */
    resume: () =>
      update({
        mode:
          get().agendaIndex >= 0 ? "agendaItem" : get().currentIndex >= 0 ? "ticket" : "agenda",
      }),
    end: () => update({ mode: "end", currentIndex: -1, agendaIndex: -1 }),
    reset: () => {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      set({
        phase: "idle",
        ownerId: "",
        deckKeys: [],
        selected: {},
        deck: [],
        mode: "agenda",
        currentIndex: -1,
        summaries: {},
        events: null,
        releasedInternal: {},
        notes: {},
        shownAttachment: null,
        focusedComment: null,
        deckGroups: {},
        deckGroupBy: "none",
        agendaItems: [],
        agendaObjectKey: null,
        agendaIndex: -1,
      });
    },
    cycleStageTheme: (hasCustomer) =>
      update({ stageTheme: nextStageTheme(get().stageTheme, hasCustomer) }),
    toggleInternalRelease: (ticketKey, commentId) => {
      const cur = get().releasedInternal[ticketKey] ?? [];
      const withdrawing = cur.includes(commentId);
      const next = withdrawing ? cur.filter((id) => id !== commentId) : [...cur, commentId];
      // Wer die Freigabe zurücknimmt, nimmt auch den Fokus darauf zurück —
      // sonst stünde er beim nächsten Freigeben unverhofft wieder groß da.
      const focus = get().focusedComment;
      const dropFocus = withdrawing && focus?.ticketKey === ticketKey && focus.id === commentId;
      update({
        releasedInternal: { ...get().releasedInternal, [ticketKey]: next },
        ...(dropFocus ? { focusedComment: null } : {}),
      });
    },
    toggleAutoReview: () => update({ autoReview: !get().autoReview }),
    setWithSummaries: (on) => update({ withSummaries: on }),
    setNote: (ticketKey, text) =>
      update({ notes: { ...get().notes, [ticketKey]: text } }),
    clearNote: (ticketKey) => {
      const next = { ...get().notes };
      delete next[ticketKey];
      update({ notes: next });
    },
    clearNotes: () => update({ notes: {} }),
    showAttachment: (a) => update(a ? { shownAttachment: a, focusedComment: null } : { shownAttachment: null }),
    focusComment: (c) => update(c ? { focusedComment: c, shownAttachment: null } : { focusedComment: null }),
    addToDeck: (ticketKey, group) => {
      if (get().deck.includes(ticketKey)) return;
      // Die Grenze gilt auch für den Nachzügler mitten im Termin: Ein Deck
      // über `DECK_MAX` scheitert sonst erst beim Protokoll (`issueKeysSchema`).
      if (get().deck.length >= DECK_MAX) return;
      update({
        deck: [...get().deck, ticketKey],
        ...(group ? { deckGroups: { ...get().deckGroups, [ticketKey]: group } } : {}),
      });
    },
    setAgendaItems: (items, objectKey) =>
      update({ agendaItems: items, agendaObjectKey: objectKey ?? null }),
    updateAgendaItem: (kind, patch) =>
      update({
        agendaItems: get().agendaItems.map((a) => (a.kind === kind ? { ...a, ...patch } : a)),
      }),
    goToAgendaItem: (i) => {
      // Dieselbe Grenze wie in `goTo`: Ein Index, den es nicht gibt, führt zu
      // einer leeren Folie auf der Bühne — und die sieht der Kunde.
      if (i < 0 || i >= enabledAgenda(get().agendaItems).length) return;
      update({ mode: "agendaItem", agendaIndex: i, shownAttachment: null, focusedComment: null });
    },
  };
});
